import type { CSSProperties } from 'react';
import { useTheme } from '../../theme';

export interface AdminLeaderboardPreviewItem {
  rank: number;
  name: string;
  pointsLabel: string;
}

export interface AdminLeaderboardPreviewProps {
  items: AdminLeaderboardPreviewItem[];
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  loading?: boolean;
  error?: boolean;
}

export function AdminLeaderboardPreview({
  items,
  loadingLabel,
  emptyLabel,
  errorLabel,
  loading,
  error,
}: AdminLeaderboardPreviewProps) {
  const theme = useTheme();
  const c = theme.palette.adminDashboard.charts;

  if (loading) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {loadingLabel}
      </p>
    );
  }

  if (error) {
    return (
      <p
        style={{
          margin: 0,
          color: theme.palette.error.main,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {errorLabel}
      </p>
    );
  }

  if (!items.length) {
    return (
      <p
        style={{
          margin: 0,
          color: c.subtitleColor,
          fontSize: theme.typography.fontSize.base,
        }}
      >
        {emptyLabel}
      </p>
    );
  }

  const listStyle: CSSProperties = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: c.rowGap,
  };

  return (
    <ul style={listStyle}>
      {items.map(row => {
        const topThree = row.rank >= 1 && row.rank <= 3;
        const circleSize = topThree ? c.rankCircleTopSize : c.rankCircleDefaultSize;
        const circleBg = topThree
          ? c.rankCircleTopBackground
          : c.rankCircleDefaultBackground;
        const circleText = topThree
          ? c.rankCircleTopText
          : c.rankCircleDefaultText;

        const rowStyle: CSSProperties = {
          display: 'flex',
          alignItems: 'center',
          gap: c.rowItemGap,
          background: c.rowBackground,
          borderRadius: c.rowBorderRadius,
          padding: c.rowPadding,
          minHeight: 44,
        };

        return (
          <li
            key={`${row.rank}-${row.name}`}
            style={rowStyle}
          >
            <div
              style={{
                width: circleSize,
                height: circleSize,
                borderRadius: theme.shape.borderRadius.full,
                background: circleBg,
                color: circleText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.semibold,
                flexShrink: 0,
              }}
              aria-hidden
            >
              {row.rank}
            </div>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.medium,
                color: c.titleColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name}
            </span>
            <span
              style={{
                flexShrink: 0,
                fontSize: theme.typography.fontSize.base,
                fontWeight: theme.typography.fontWeight.semibold,
                color: c.pointsAccent,
              }}
            >
              {row.pointsLabel}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
